import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SEO from '../components/common/SEO';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Gavel, Clock, Flame, Trophy, ArrowRight, Timer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuctionRoomPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useApp();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchAuctions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auctions`);
      if (res.ok) {
        const data = await res.json();
        setAuctions(data);
      }
    } catch (err) {
      console.error('Error fetching auctions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuctions();
    // Poll every 15 seconds for live updates
    const interval = setInterval(fetchAuctions, 15000);
    return () => clearInterval(interval);
  }, [fetchAuctions]);

  const activeAuctions = auctions.filter(a => a.status === 'active');
  const upcomingAuctions = auctions.filter(a => a.status === 'approved');
  const endedAuctions = auctions.filter(a => a.status === 'sold' || a.status === 'ended');

  const filtered = filter === 'active' ? activeAuctions
    : filter === 'upcoming' ? upcomingAuctions
    : filter === 'ended' ? endedAuctions
    : auctions;

  return (
    <Layout title="Auction Room">
      <SEO
        title="Auction Room"
        description="Bid on unique curated items in the Minted Auction Room. Weekly auctions with exclusive deals."
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-amber-900 via-amber-800 to-yellow-900 text-white px-4 py-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIi8+PC9zdmc+')] pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-yellow-500/30 rounded-xl flex items-center justify-center" style={{boxShadow: '0 0 20px rgba(234,179,8,0.3)'}}>
              <Gavel className="w-5 h-5 text-yellow-300" />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{textShadow: '0 0 20px rgba(234,179,8,0.4)'}}>
                Auction Room
              </h1>
              <p className="text-amber-200 text-sm">Bid on curated unique items</p>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-amber-400/20">
              <p className="text-2xl font-bold text-yellow-300">{activeAuctions.length}</p>
              <p className="text-[10px] text-amber-200">Live Now</p>
            </div>
            <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-amber-400/20">
              <p className="text-2xl font-bold text-yellow-300">{upcomingAuctions.length}</p>
              <p className="text-[10px] text-amber-200">Upcoming</p>
            </div>
            <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-amber-400/20">
              <p className="text-2xl font-bold text-yellow-300">{endedAuctions.length}</p>
              <p className="text-[10px] text-amber-200">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide bg-white border-b">
        {[
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Live Now', icon: Flame },
          { key: 'upcoming', label: 'Upcoming', icon: Clock },
          { key: 'ended', label: 'Completed', icon: Trophy },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
              filter === tab.key
                ? 'bg-amber-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`auction-filter-${tab.key}`}
          >
            {tab.icon && <tab.icon className="w-3.5 h-3.5" />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Auction List */}
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="flex flex-col items-center py-16">
            <div className="w-10 h-10 border-3 border-amber-300 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-gray-500 text-sm">Loading auctions...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <Gavel className="w-16 h-16 text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No auctions yet</h2>
            <p className="text-gray-500 text-sm max-w-xs">
              {filter === 'active' ? 'No live auctions right now. Check back soon!' :
               filter === 'upcoming' ? 'No upcoming auctions scheduled yet.' :
               'Curated items will be auctioned here soon. Stay tuned!'}
            </p>
          </div>
        ) : (
          filtered.map(auction => (
            <AuctionCard
              key={auction.id}
              auction={auction}
              onClick={() => navigate(`/auction/${auction.id}`)}
            />
          ))
        )}
      </div>
    </Layout>
  );
};

const AuctionCard = ({ auction, onClick }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const target = auction.status === 'approved' ? auction.start_time : auction.end_time;
      const diff = new Date(target) - new Date();
      if (diff <= 0) {
        setTimeLeft(auction.status === 'approved' ? 'Starting soon...' : 'Ended');
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [auction]);

  const isActive = auction.status === 'active';
  const isUpcoming = auction.status === 'approved';
  const isSold = auction.status === 'sold';

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow text-left border border-gray-100"
      data-testid={`auction-card-${auction.id}`}
    >
      <div className="flex">
        <div className="w-28 h-28 flex-shrink-0 relative">
          {auction.product_image ? (
            <img src={auction.product_image} alt={auction.product_title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <Gavel className="w-8 h-8 text-gray-400" />
            </div>
          )}
          {isActive && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-red-500 text-white text-[10px] animate-pulse">LIVE</Badge>
            </div>
          )}
          {isUpcoming && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-blue-500 text-white text-[10px]">UPCOMING</Badge>
            </div>
          )}
          {isSold && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-green-600 text-white text-[10px]">SOLD</Badge>
            </div>
          )}
        </div>
        <div className="flex-1 p-3 flex flex-col justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm line-clamp-1">{auction.product_title}</h3>
            <div className="flex items-center gap-2 mt-1">
              {(isActive || isSold) && auction.current_bid > 0 ? (
                <p className="text-lg font-bold text-amber-600">${auction.current_bid.toFixed(2)}</p>
              ) : (
                <p className="text-lg font-bold text-gray-600">${auction.minimum_price?.toFixed(2)}</p>
              )}
              <span className="text-[10px] text-gray-400">
                {isActive ? 'current bid' : isSold ? 'winning bid' : 'starting price'}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Timer className="w-3.5 h-3.5" />
              <span className={isActive ? 'text-red-500 font-medium' : ''}>
                {isSold || auction.status === 'ended' ? 'Auction ended' : timeLeft}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {auction.bid_count > 0 && (
                <span className="text-[10px] text-gray-500">{auction.bid_count} bid{auction.bid_count !== 1 ? 's' : ''}</span>
              )}
              <ArrowRight className="w-4 h-4 text-amber-500" />
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

export default AuctionRoomPage;
